import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { statutDe } from "./queries";

/**
 * Les ecrans qui ne montrent pas de portefeuille : chargement, porte fermee, page
 * inconnue, panne. Ils sont au meme endroit parce que les trois pages de la console les
 * partagent, et qu'ils doivent dire la meme chose partout.
 */

/**
 * Vrai quand l'ecran d'erreur doit prendre la place de la page. Les pages qui attendent le
 * commit d'une demande « C'est fait » se relisent toutes les 15 s : une relecture qui echoue
 * (backend qui redemarre, reseau coupe) laisse la derniere lecture a l'ecran, et la suivante
 * repart 15 s plus tard. Seules une session perdue (401) ou une adresse refusee (403) la
 * remplacent, comme une premiere lecture qui echoue : montrer encore la page serait montrer
 * ce que la console refuse desormais.
 */
export function panneAffichee(lecture: {
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: unknown;
}): boolean {
  if (!lecture.isError) return false;
  const statut = statutDe(lecture.error);
  return lecture.data === undefined || statut === 401 || statut === 403;
}

/** Un identifiant d'adresse : un entier positif, sinon la page est introuvable sans rien lire. */
export function idValide(id: string): boolean {
  return /^[1-9]\d{0,9}$/.test(id);
}

/** Ce qu'un ecran montre d'une lecture : l'ecran d'erreur, le bandeau, le contenu. */
export type Affichage = {
  readonly ecranDErreur: boolean;
  readonly bandeau: boolean;
  readonly contenu: boolean;
};

/**
 * Jamais l'ecran d'erreur et le contenu ensemble. Une relecture (apres une ecriture, au
 * retour sur l'ecran) peut echouer alors que la lecture precedente est encore en cache :
 *
 * - 401 ou 403 : l'ecran d'erreur seul. La session ou l'adresse ne passe plus, montrer la
 *   lecture precedente serait montrer ce que la console refuse desormais ;
 * - une autre erreur, avec une lecture precedente : son contenu reste, sous un bandeau qui
 *   dit l'echec et son message ;
 * - une erreur sans lecture precedente : l'ecran d'erreur.
 *
 * Ne avec le coloriage, partage avec les histoires depuis #226 (un module n'importe jamais un
 * autre module).
 */
export function affichageDeLecture(lecture: {
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: unknown;
}): Affichage {
  const precedente = lecture.data !== undefined;
  if (!lecture.isError) return { ecranDErreur: false, bandeau: false, contenu: precedente };
  const statut = statutDe(lecture.error);
  if (statut === 401 || statut === 403 || !precedente) {
    return { ecranDErreur: true, bandeau: false, contenu: false };
  }
  return { ecranDErreur: false, bandeau: true, contenu: true };
}

/**
 * L'encadre des pannes : relecture echouee, et dans les histoires Lab a l'arret, session perdue,
 * refus. Sa teinte est opaque, melangee a la carte, et son texte est le rouge rapproche de
 * l'encre : le rouge du template sur sa teinte transparente a 10 % tombait a 4,22:1 sur le fond
 * de page du telephone en theme clair, sous le seuil de 4,5:1 (relecture de la PR #239). L'e2e
 * `histoires.spec.ts` le mesure dans les deux themes.
 */
export const ENCADRE_PANNE =
  "rounded-md border border-destructive/40 bg-[color:color-mix(in_oklab,var(--color-destructive)_8%,var(--color-card))] px-3.5 py-2.5 text-sm text-[color:color-mix(in_oklab,var(--color-destructive)_80%,var(--color-foreground))]";

/** Le bandeau d'une relecture qui a echoue, au-dessus de la lecture precedente. */
export function RelectureEchouee({ error }: { error: unknown }) {
  const message = error instanceof Error && error.message !== "" ? error.message : null;
  return (
    <p className={ENCADRE_PANNE} role="alert">
      La console n'a pas pu relire cet écran{message === null ? "" : ` (${message})`} : ce qui
      s'affiche date de la dernière lecture réussie.
    </p>
  );
}

export function Chargement({ quoi }: { quoi: string }) {
  return (
    <p className="text-sm text-muted-foreground" role="status">
      Chargement {quoi}...
    </p>
  );
}

/**
 * Ce qu'il faut afficher a la place de la page. `null` quand l'erreur n'est pas de
 * celles-la : c'est alors a l'appelant de decider (la liste, par exemple, reste
 * affichable avec un avertissement).
 */
export function EcranDErreur({ error, quoi }: { error: unknown; quoi: string }) {
  const statut = statutDe(error);
  if (statut === 401) return <Connexion />;
  if (statut === 403) return <Refus />;
  if (statut === 404) return <Introuvable quoi={quoi} />;
  return <Panne quoi={quoi} />;
}

/** 403 : la session est valide, l'adresse n'est pas admise. */
export function Refus() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Cette adresse n'est pas sur la liste</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
        <p>
          La console est un outil interne. Seules les adresses inscrites dans la liste blanche de
          l'app y entrent.
        </p>
        <p>
          La liste se pose à la provision, dans le fichier d'environnement de l'app (variable de
          dépôt <code>LAB_ADMIN_EMAIL</code>).
        </p>
      </CardContent>
    </Card>
  );
}

/** 404 : le slug ou l'identifiant ne designe rien. */
export function Introuvable({ quoi }: { quoi: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rien sous ce nom</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>Le portefeuille ne connaît pas {quoi}.</p>
        <p>
          <Link className="underline underline-offset-2" to="/">
            Revenir au portefeuille
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

/** Tout le reste : backend muet, contrat rompu, panne reseau. */
export function Panne({ quoi }: { quoi: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lecture impossible</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        Le backend n'a pas rendu {quoi}. La page est à jour dès qu'il répond de nouveau.
      </CardContent>
    </Card>
  );
}

type EtatEnvoi = "repos" | "envoi" | "parti" | "ferme" | "echec";

/**
 * 401 : personne n'est connecte. Le template n'a pas encore d'ecran de connexion
 * (`src/lab/` ne pose que les pages legales et « Mes données »), donc la console porte
 * le sien : une adresse, un lien magique, rien d'autre. Le jour ou le template en livre
 * un, ce composant se remplace par une redirection vers lui.
 *
 * La liste blanche n'est pas appliquee ici et ne peut pas l'etre : elle est verifiee a la
 * lecture, pas a l'envoi du lien (accroche manquante du template, voir `CLAUDE.app.md`).
 * Une adresse etrangere recevra donc un lien, et ne verra rien : 403 sur tout.
 */
export function Connexion() {
  const [adresse, setAdresse] = useState("");
  const [etat, setEtat] = useState<EtatEnvoi>("repos");

  const demander = async (): Promise<void> => {
    setEtat("envoi");
    try {
      const response = await fetch("/api/auth/sign-in/magic-link", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: adresse, callbackURL: "/" }),
      });
      if (response.ok) {
        setEtat("parti");
      } else {
        setEtat(response.status === 503 ? "ferme" : "echec");
      }
    } catch {
      setEtat("echec");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connexion requise</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          La console envoie un lien de connexion par courrier électronique. Il est valable quinze
          minutes et n'ouvre la page que pour l'adresse inscrite sur la liste.
        </p>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void demander();
          }}
        >
          <input
            aria-label="Adresse électronique"
            autoComplete="email"
            className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm"
            onChange={(event) => setAdresse(event.target.value)}
            placeholder="adresse@petitlab.fr"
            type="email"
            value={adresse}
          />
          <Button disabled={etat === "envoi" || adresse.trim() === ""} type="submit">
            Recevoir le lien
          </Button>
        </form>
        {etat === "parti" ? (
          <p className="text-sm">Le lien est parti. Regarde ta boîte, il vaut quinze minutes.</p>
        ) : null}
        {etat === "ferme" ? (
          <p className="text-sm text-destructive">
            L'authentification n'est pas configurée sur ce serveur (AUTH_SECRET absent).
          </p>
        ) : null}
        {etat === "echec" ? (
          <p className="text-sm text-destructive">
            L'envoi n'a pas abouti. Réessaie dans quelques minutes.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
